import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Footprints, LogOut, Search, Users, MailQuestion, Inbox, Clock, MapPin, Globe, Mail, MessageSquare,
} from "lucide-react";

const statusColors = {
  new: "bg-primary/10 text-primary",
  reviewed: "bg-emerald-100 text-emerald-700",
  archived: "bg-secondary text-muted-foreground",
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex rounded-md px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide ${statusColors[status] || statusColors.new}`}>
    {status}
  </span>
);

const ChannelChips = ({ email, sms }) => (
  <div className="flex gap-1.5">
    {email && <span className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary"><Mail className="h-3 w-3" /> Email</span>}
    {sms && <span className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary"><MessageSquare className="h-3 w-3" /> SMS</span>}
    {!email && !sms && <span className="text-xs text-muted-foreground">—</span>}
  </div>
);

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("submissions");
  const [stats, setStats] = useState(null);
  const [regs, setRegs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);

  const loadStats = useCallback(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  const loadRegs = useCallback(() => {
    api.get("/registrations", { params: { q: q || undefined, status: statusFilter } })
      .then((r) => setRegs(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [q, statusFilter]);
  const loadContacts = useCallback(() => {
    api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadStats(); loadContacts(); }, [loadStats, loadContacts]);
  useEffect(() => { const t = setTimeout(loadRegs, 250); return () => clearTimeout(t); }, [loadRegs]);

  const changeStatus = async (id, status, type = "reg") => {
    try {
      const path = type === "reg" ? `/registrations/${id}/status` : `/contacts/${id}/status`;
      await api.patch(path, { status });
      toast.success("Status updated.");
      if (type === "reg") { loadRegs(); loadStats(); setSelected((s) => s && s.id === id ? { ...s, status } : s); }
      else { loadContacts(); setSelectedContact((s) => s && s.id === id ? { ...s, status } : s); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doLogout = () => { logout(); navigate("/login"); };
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 lg:px-8">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Footprints className="h-5 w-5" strokeWidth={2.5} />
            </span>
            <span className="font-heading text-lg font-bold tracking-tight text-foreground">Step<span className="text-primary">Zone</span></span>
            <Badge variant="secondary" className="ml-1 rounded-md font-body uppercase tracking-wide">Owner</Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden font-body text-sm text-muted-foreground sm:inline">{user?.email}</span>
            <Button variant="outline" size="sm" className="rounded-lg border-2 font-semibold" onClick={doLogout} data-testid="logout-button">
              <LogOut className="mr-1.5 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-8 lg:px-8">
        <h1 className="font-heading text-3xl font-bold tracking-tighter text-foreground">Dashboard</h1>
        <p className="mt-1 font-body text-muted-foreground">Review submissions and per-channel consent.</p>

        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard icon={Users} label="Total Submissions" value={stats?.total_registrations} testid="stat-total" />
          <StatCard icon={Inbox} label="New Submissions" value={stats?.new_registrations} testid="stat-new" />
          <StatCard icon={MailQuestion} label="Contact Messages" value={stats?.total_contacts} testid="stat-contacts" />
          <StatCard icon={Clock} label="New Messages" value={stats?.new_contacts} testid="stat-new-contacts" />
        </div>

        <div className="mt-8">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="dashboard-tabs">
              <TabsTrigger value="submissions" data-testid="tab-submissions">Submissions</TabsTrigger>
              <TabsTrigger value="contacts" data-testid="tab-contacts">Contact Messages</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {tab === "submissions" && (
          <div className="mt-4 rounded-2xl border border-border bg-card">
            <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative w-full sm:max-w-xs">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, goal…" className="rounded-lg border-2 border-input pl-9" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full rounded-lg border-2 border-input sm:w-44" data-testid="status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="reviewed">Reviewed</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Goal</TableHead>
                    <TableHead>Timeframe</TableHead>
                    <TableHead>Channels</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {regs.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">No submissions found.</TableCell></TableRow>
                  ) : regs.map((r) => (
                    <TableRow key={r.id} className="cursor-pointer" onClick={() => setSelected(r)} data-testid={`reg-row-${r.id}`}>
                      <TableCell className="font-semibold text-foreground">{r.first_name} {r.last_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{r.goal}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{r.timeframe}</TableCell>
                      <TableCell><ChannelChips email={r.email_optin} sms={r.sms_optin} /></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmt(r.created_at)}</TableCell>
                      <TableCell><StatusBadge status={r.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {tab === "contacts" && (
          <div className="mt-4 rounded-2xl border border-border bg-card">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Received</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">No messages yet.</TableCell></TableRow>
                  ) : contacts.map((c) => (
                    <TableRow key={c.id} className="cursor-pointer" onClick={() => setSelectedContact(c)} data-testid={`contact-row-${c.id}`}>
                      <TableCell className="font-semibold text-foreground">{c.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{c.email}</TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-muted-foreground">{c.message}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmt(c.created_at)}</TableCell>
                      <TableCell><StatusBadge status={c.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>

      {/* Submission detail */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="reg-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-foreground">{selected.first_name} {selected.last_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Detail label="Goal" value={selected.goal} />
                  <Detail label="Timeframe" value={selected.timeframe} />
                  <Detail label="Email" value={selected.email || "—"} />
                  <Detail label="Phone" value={selected.phone || "—"} />
                </div>
                <div>
                  <p className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Opted-in channels</p>
                  <div className="mt-1"><ChannelChips email={selected.email_optin} sms={selected.sms_optin} /></div>
                </div>

                <div className="rounded-xl border border-border bg-secondary p-4">
                  <p className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Consent Proof</p>
                  <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                    <p className="flex items-center gap-2"><Clock className="h-4 w-4" /> {fmt(selected.consent_timestamp)}</p>
                    <p className="flex items-center gap-2"><MapPin className="h-4 w-4" /> IP: {selected.ip_address}</p>
                    <p className="flex items-start gap-2 break-all"><Globe className="mt-0.5 h-4 w-4 shrink-0" /> {selected.source_url || "—"}</p>
                    {selected.email_consent_text && (
                      <div className="mt-2 rounded-lg bg-card p-3 text-xs leading-relaxed">
                        <span className="mb-1 flex items-center gap-1 font-semibold text-primary"><Mail className="h-3 w-3" /> Email consent</span>
                        {selected.email_consent_text}
                      </div>
                    )}
                    {selected.sms_consent_text && (
                      <div className="mt-2 rounded-lg bg-card p-3 text-xs leading-relaxed">
                        <span className="mb-1 flex items-center gap-1 font-semibold text-primary"><MessageSquare className="h-3 w-3" /> SMS consent</span>
                        {selected.sms_consent_text}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Status:</span>
                  <Select value={selected.status} onValueChange={(v) => changeStatus(selected.id, v, "reg")}>
                    <SelectTrigger className="w-40 rounded-lg border-2 border-input" data-testid="reg-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="reviewed">Reviewed</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Contact detail */}
      <Dialog open={!!selectedContact} onOpenChange={(o) => !o && setSelectedContact(null)}>
        <DialogContent className="max-w-lg" data-testid="contact-detail-dialog">
          {selectedContact && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-foreground">{selectedContact.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <Detail label="Email" value={selectedContact.email} />
                <Detail label="Phone" value={selectedContact.phone || "—"} />
                <div>
                  <p className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Message</p>
                  <p className="mt-2 whitespace-pre-wrap rounded-xl border border-border bg-secondary p-4 text-foreground">{selectedContact.message}</p>
                </div>
                <p className="text-muted-foreground">Received: {fmt(selectedContact.created_at)}</p>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Status:</span>
                  <Select value={selectedContact.status} onValueChange={(v) => changeStatus(selectedContact.id, v, "contact")}>
                    <SelectTrigger className="w-40 rounded-lg border-2 border-input" data-testid="contact-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="reviewed">Reviewed</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">{label}</span>
      <Icon className="h-4 w-4 text-primary" />
    </div>
    <p className="mt-2 font-heading text-3xl font-bold tracking-tight text-foreground">{value ?? "—"}</p>
  </div>
);

const Detail = ({ label, value }) => (
  <div>
    <p className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
    <p className="mt-0.5 font-medium text-foreground">{value}</p>
  </div>
);
